// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import api from 'services/api';
import {
  avaliar, derivar, ehCampo, errosDaEtapa, etapasVisiveis, expandir, interpolar,
  mascaraCep, mascaraCpf, opcoesDoCampo, paisDoEndereco, soDigitos, valoresPadrao,
} from './motor';
import { Aviso, Botao, Caixa, Campo, Cartao, Emails, Fim, Grade, Passos, Revisao, Rodape, Secao, Upload } from './styles';

/**
 * Formulário nativo de procuração (substitui o Typeform). A configuração
 * vem do cj-automations, pela API da Central:
 * POST central/processes/{id}/procuracao-nativa/{iniciar|rascunho|ler-documento|enviar}.
 * O rascunho é salvo sozinho a cada mudança (o cliente pode sair e voltar).
 */

const SELO = { doc: 'Lido do documento — confira', cep: 'Preenchido pelo CEP' };

const formatarData = (v) => (/^\d{4}-\d{2}-\d{2}$/.test(v || '') ? v.split('-').reverse().join('/') : v);

function linhaEndereco(r, p) {
  if (r[`${p}Pais`] === 'Brasil') {
    const rua = [r[`${p}Logradouro`], r[`${p}Numero`], r[`${p}Complemento`]].filter(Boolean).join(', ');
    return [rua, r[`${p}Bairro`], r[`${p}Cidade`], r[`${p}Cep`] && `CEP ${r[`${p}Cep`]}`].filter(Boolean).join(' — ');
  }
  return [r[`${p}EndExterior`], r[`${p}CidadeExterior`], r[`${p}CodPostal`], paisDoEndereco(r, p)].filter(Boolean).join(', ');
}

/**
 * Foto de celular (3–8 MB) passa do limite de upload do servidor e chegava
 * vazia. Reduz no navegador: lado maior até 2000 px, JPEG — continua
 * legível para a leitura e fica com algumas centenas de KB. PDF e formatos
 * que o navegador não abre (HEIC fora do Safari) seguem como vieram.
 */
async function reduzirImagem(arquivo, ladoMaximo = 2000, limiteBytes = 1800 * 1024) {
  if (!/^image\/(jpeg|png|webp|heic|heif)$/.test(arquivo.type) || arquivo.size <= 600 * 1024) return arquivo;
  try {
    const url = URL.createObjectURL(arquivo);
    const img = await new Promise((ok, erro) => { const i = new Image(); i.onload = () => ok(i); i.onerror = erro; i.src = url; });
    URL.revokeObjectURL(url);
    const escala = Math.min(1, ladoMaximo / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * escala);
    canvas.height = Math.round(img.height * escala);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    for (const qualidade of [0.85, 0.75, 0.6]) {
      const blob = await new Promise((ok) => canvas.toBlob(ok, 'image/jpeg', qualidade));
      if (blob && (blob.size <= limiteBytes || qualidade === 0.6)) {
        return new File([blob], arquivo.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
      }
    }
  } catch (e) {
    // não conseguiu abrir a imagem (ex.: HEIC no Chrome): manda a original
  }
  return arquivo;
}

export function FormularioNativo({ processId }) {
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');
  const [formulario, setFormulario] = useState(null);
  const [contexto, setContexto] = useState(null);
  const [r, setR] = useState({});
  const [ia, setIa] = useState({});
  const [uploads, setUploads] = useState({});
  const [passo, setPasso] = useState('inicio');
  const [visitados, setVisitados] = useState({ inicio: true });
  const [tentou, setTentou] = useState({});
  const [voltarRevisao, setVoltarRevisao] = useState(false);
  const [emailsExtras, setEmailsExtras] = useState([]);
  const [emailNovo, setEmailNovo] = useState('');
  const [erroEmail, setErroEmail] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState('');
  const [aviso, setAviso] = useState('');
  const salvarTimer = useRef(null);
  const topo = useRef(null);
  const base = `central/processes/${processId}/procuracao-nativa`;

  useEffect(() => {
    let ativo = true;
    api.post(`${base}/iniciar`)
      .then(({ data }) => {
        if (!ativo) return;
        setFormulario(data.formulario);
        setContexto(data.contexto);
        setR({ ...valoresPadrao(data.formulario), ...(data.respostas || {}) });
        const enviados = {};
        (data.anexos || []).forEach((a) => { enviados[`${a.documento}:${a.prefixo || ''}`] = { ok: true, nome: a.nome }; });
        setUploads(enviados);
      })
      .catch((e) => ativo && setErroCarga(e?.response?.data?.message || 'Não conseguimos abrir o formulário agora. Tente de novo em instantes.'))
      .finally(() => ativo && setCarregando(false));
    return () => { ativo = false; };
  }, [base]);

  // Rascunho: salva 1,5 s depois da última mudança.
  const salvarRascunho = useCallback((respostas) => {
    clearTimeout(salvarTimer.current);
    salvarTimer.current = setTimeout(() => { api.post(`${base}/rascunho`, { respostas }).catch(() => {}); }, 1500);
  }, [base]);

  const d = useMemo(() => (formulario && contexto ? derivar(formulario, r, contexto) : {}), [formulario, contexto, r]);
  const etapas = useMemo(() => (formulario ? etapasVisiveis(formulario, r, d) : []), [formulario, r, d]);
  const ordem = useMemo(() => ['inicio', ...etapas.map((e) => e.id), 'revisao'], [etapas]);
  const atual = passo === 'fim' ? 'fim' : ordem.includes(passo) ? passo : 'revisao';
  const etapa = etapas.find((e) => e.id === atual);

  const mudar = useCallback((chave, valor, origem) => {
    setR((anterior) => {
      const novo = { ...anterior, [chave]: valor };
      salvarRascunho(novo);
      return novo;
    });
    setIa((anterior) => ({ ...anterior, [chave]: origem || false }));
  }, [salvarRascunho]);

  const preencher = useCallback((campos, origem) => {
    setR((anterior) => {
      const novo = { ...anterior, ...campos };
      salvarRascunho(novo);
      return novo;
    });
    setIa((anterior) => ({ ...anterior, ...Object.fromEntries(Object.keys(campos).map((k) => [k, origem])) }));
  }, [salvarRascunho]);

  const buscarCep = useCallback((prefixo, cep) => {
    fetch(`https://viacep.com.br/ws/${cep}/json/`)
      .then((resp) => resp.json())
      .then((end) => {
        if (!end || end.erro) { setAviso(`cep:${prefixo}:nao`); return; }
        preencher({
          [`${prefixo}Logradouro`]: end.logradouro || '',
          [`${prefixo}Bairro`]: end.bairro || '',
          [`${prefixo}Cidade`]: [end.localidade, end.uf].filter(Boolean).join('/'),
        }, 'cep');
        setAviso(`cep:${prefixo}:${end.logradouro ? 'ok' : 'geral'}`);
      })
      .catch(() => setAviso(`cep:${prefixo}:nao`));
  }, [preencher]);

  const anexar = useCallback(async (upload, arquivo) => {
    const chave = `${upload.documento}:${upload.prefixo || ''}`;
    setUploads((u) => ({ ...u, [chave]: { lendo: true, nome: arquivo.name } }));
    const fd = new FormData();
    const enviar = await reduzirImagem(arquivo);
    fd.append('file', enviar, enviar.name);
    fd.append('documento', upload.documento);
    if (upload.prefixo) fd.append('prefixo', upload.prefixo);
    try {
      const { data } = await api.post(`${base}/ler-documento`, fd);
      preencher(data.campos || {}, 'doc');
      setUploads((u) => ({ ...u, [chave]: { ok: true, nome: arquivo.name, n: Object.keys(data.campos || {}).length, lido: data.lido } }));
    } catch (e) {
      setUploads((u) => ({ ...u, [chave]: { erro: e?.response?.data?.message || 'Não conseguimos receber o arquivo. Tente de novo ou preencha abaixo.', nome: arquivo.name } }));
    }
  }, [base, preencher]);

  const ir = (id) => {
    setPasso(id);
    setVisitados((v) => ({ ...v, [id]: true }));
    if (topo.current) topo.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const errosAtuais = etapa ? errosDaEtapa(etapa, r, d) : {};
  const nFaltam = Object.keys(errosAtuais).length;

  const continuar = () => {
    if (atual === 'revisao') { enviar(); return; }
    if (etapa && nFaltam) { setTentou((t) => ({ ...t, [etapa.id]: true })); return; }
    const i = ordem.indexOf(atual);
    const prox = voltarRevisao ? 'revisao' : ordem[i + 1];
    setVoltarRevisao(false);
    ir(prox);
  };

  const enviar = async () => {
    setEnviando(true);
    setErroEnvio('');
    try {
      await api.post(`${base}/enviar`, { respostas: r, emailsExtras });
      ir('fim');
    } catch (e) {
      const erros = e?.response?.data?.erros;
      if (erros && erros.length) {
        setTentou((t) => ({ ...t, ...Object.fromEntries(erros.map((x) => [x.etapa, true])) }));
        setErroEnvio('Faltam alguns dados — levamos você até eles.');
        ir(erros[0].etapa);
      } else {
        setErroEnvio(e?.response?.data?.message || 'Não conseguimos enviar agora. Tente de novo em instantes.');
      }
    } finally {
      setEnviando(false);
    }
  };

  const adicionarEmail = () => {
    const e = emailNovo.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { setErroEmail('Confira o e-mail — parece incompleto.'); return; }
    if (e === contexto?.email || emailsExtras.includes(e)) { setErroEmail('Esse e-mail já vai receber.'); return; }
    setEmailsExtras((l) => [...l, e]);
    setEmailNovo('');
    setErroEmail('');
  };

  if (carregando) return <Caixa><Cartao><p className="lead">Abrindo o formulário…</p></Cartao></Caixa>;
  if (erroCarga) return <Caixa><Cartao><Aviso className="atencao">{erroCarga}</Aviso></Cartao></Caixa>;

  const t = (texto) => interpolar(texto, d);

  const ajudaCep = (prefixo) => {
    if (aviso === `cep:${prefixo}:ok`) return 'Endereço encontrado. Confira e complete o número.';
    if (aviso === `cep:${prefixo}:geral`) return 'CEP da cidade toda: preencha rua e bairro.';
    if (aviso === `cep:${prefixo}:nao`) return 'Não achamos esse CEP. Confira os números ou preencha o endereço abaixo.';
    return 'Digite o CEP e a gente preenche rua, bairro e cidade.';
  };

  const renderItem = (item, idx) => {
    if (!avaliar(item.visivelSe, r, d)) return null;
    if (item.tipo === 'secao') {
      return <Secao key={idx} className={idx === 0 ? 'primeira' : ''}><h3>{t(item.titulo)}</h3>{item.sub && <p>{t(item.sub)}</p>}</Secao>;
    }
    if (item.tipo === 'aviso') return <Aviso key={idx} className={item.estilo} role="status">{t(item.texto)}</Aviso>;
    if (item.tipo === 'upload') {
      const u = uploads[`${item.documento}:${item.prefixo || ''}`] || {};
      return (
        <Upload key={idx} className={u.ok ? 'ok' : ''}>
          <div className="txt">
            <h4>{t(item.titulo)}</h4>
            {u.lendo && <p>Lendo {u.nome}…</p>}
            {u.ok && <p><b>{u.nome}</b> · {u.lido === false ? 'recebido. Não conseguimos ler os dados — preencha abaixo.' : `lido. ${u.n ? `${u.n} campos preenchidos — confira abaixo.` : ''}`}</p>}
            {u.erro && <p className="erro">{u.erro}</p>}
            {!u.lendo && !u.ok && !u.erro && <p>{t(item.texto)}</p>}
          </div>
          <label className={`${u.ok ? '' : ''}`}>
            <Botao as="span" className="sec">{u.ok ? 'Trocar arquivo' : 'Anexar arquivo'}</Botao>
            <input type="file" accept="image/*,application/pdf" disabled={u.lendo}
              onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) anexar(item, f); e.target.value = ''; }} />
          </label>
        </Upload>
      );
    }
    if (!ehCampo(item)) return null;

    const id = `pn_${item.chave}`;
    const valor = r[item.chave] ?? '';
    const erro = errosAtuais[item.chave];
    const formatoRuim = erro && valor !== '' && erro !== 'Preencha este campo';
    const mostrarErro = erro && (tentou[atual] || formatoRuim);
    const selo = ia[item.chave] ? SELO[ia[item.chave]] : '';
    const cls = `${item.meia ? '' : 'cheio '}${mostrarErro ? 'com-erro ' : ''}${selo ? 'ia' : ''}`;
    const rotulo = t(item.rotulo);
    const ajuda = item.tipo === 'cep' ? ajudaCep(item.enderecoDe) : item.ajuda && t(item.ajuda);

    if (item.tipo === 'check') {
      return (
        <Campo key={idx} className={cls}>
          <label className="check"><input type="checkbox" checked={!!valor} onChange={() => mudar(item.chave, !valor)} />{rotulo}</label>
        </Campo>
      );
    }
    if (item.tipo === 'opcoes') {
      return (
        <Campo key={idx} className={cls}>
          <fieldset>
            <legend className="rot">{rotulo}{selo && <span className="selo">{selo}</span>}</legend>
            <div className="pills">
              {opcoesDoCampo(item, d).map((op) => (
                <button key={op} type="button" className={valor === op ? 'on' : ''} aria-pressed={valor === op} onClick={() => mudar(item.chave, op)}>{op}</button>
              ))}
            </div>
            {ajuda && <p className="ajuda">{ajuda}</p>}
            {mostrarErro && <p className="erro">{erro}</p>}
          </fieldset>
        </Campo>
      );
    }
    const tipoInput = item.tipo === 'data' ? 'date' : item.tipo === 'hora' ? 'time' : 'text';
    const aoDigitar = (e) => {
      let v = e.target.value;
      if (item.tipo === 'cpf') v = mascaraCpf(v);
      if (item.tipo === 'cep') {
        v = mascaraCep(v);
        if (soDigitos(v).length === 8 && soDigitos(v) !== soDigitos(valor)) buscarCep(item.enderecoDe, soDigitos(v));
      }
      mudar(item.chave, v);
    };
    return (
      <Campo key={idx} className={cls}>
        <label className="rot" htmlFor={id}>{rotulo}{item.opcional && <span className="opc"> (opcional)</span>}{selo && <span className="selo">{selo}</span>}</label>
        <input id={id} type={tipoInput} value={valor} placeholder={item.placeholder || ''} onChange={aoDigitar} aria-invalid={!!mostrarErro}
          inputMode={item.tipo === 'cpf' || item.tipo === 'cep' ? 'numeric' : undefined} />
        {ajuda && <p className="ajuda">{ajuda}</p>}
        {mostrarErro && <p className="erro">{erro}</p>}
      </Campo>
    );
  };

  /** Revisão: por etapa, os campos visíveis (endereço numa linha só). */
  const blocosRevisao = etapas.map((e) => {
    const linhas = [];
    const enderecos = new Set();
    expandir(e.itens).forEach((i) => {
      if (i.tipo === 'secao' && avaliar(i.visivelSe, r, d)) { linhas.push({ secao: true, r: t(i.titulo), v: '' }); return; }
      if (!ehCampo(i) || !avaliar(i.visivelSe, r, d)) return;
      if (i.enderecoDe) {
        if (!enderecos.has(i.enderecoDe)) { enderecos.add(i.enderecoDe); linhas.push({ r: 'Endereço', v: linhaEndereco(r, i.enderecoDe) }); }
        return;
      }
      const v = r[i.chave];
      if (i.tipo === 'check') { if (v) linhas.push({ r: t(i.rotulo), v: 'Sim' }); return; }
      if (v === undefined || v === '') return;
      linhas.push({ r: t(i.rotulo), v: i.tipo === 'data' ? formatarData(v) : String(v) });
    });
    // Subtítulo sem nenhum dado abaixo (ex.: "Responsável legal" vazio) não aparece.
    const limpas = linhas.filter((l, n) => !l.secao || (linhas[n + 1] && !linhas[n + 1].secao));
    return { id: e.id, titulo: t(e.titulo), linhas: limpas };
  });

  const nProc = formulario.codigo === 'transcricao' ? d.nProcuracoes : 1;

  return (
    <Caixa ref={topo}>
      {atual !== 'fim' && (
        <Passos aria-label="Etapas do formulário">
          {ordem.map((id, n) => {
            const rot = id === 'inicio' ? 'Início' : id === 'revisao' ? 'Revisão' : etapas.find((e) => e.id === id)?.rotulo;
            const cls = id === atual ? 'atual' : visitados[id] ? 'feito' : '';
            return <button key={id} type="button" className={cls} aria-current={id === atual ? 'step' : undefined} onClick={() => visitados[id] && ir(id)}>{n + 1}. {rot}</button>;
          })}
        </Passos>
      )}
      <Cartao>
        {atual === 'inicio' && (
          <>
            <h2>Vamos gerar a procuração</h2>
            <p className="lead">Com esses dados a gente gera a procuração e faz o cadastro do pedido na conservatória, em Portugal. Leva uns 5 minutos, e dá para parar e continuar depois: o que você preencher fica salvo.</p>
            <p><b>Tenha em mãos (foto ou PDF):</b></p>
            <ul>
              {formulario.precisa.filter((p) => avaliar(p.visivelSe, r, d)).map((p) => <li key={p.texto}>{t(p.texto)}</li>)}
            </ul>
            <Aviso>Anexando os documentos, a gente lê e preenche o que der. Você só confere e corrige se precisar.</Aviso>
          </>
        )}

        {etapa && (
          <>
            <h2>{t(etapa.titulo)}</h2>
            {etapa.lead && <p className="lead">{t(etapa.lead)}</p>}
            <Grade>{expandir(etapa.itens).map(renderItem)}</Grade>
          </>
        )}

        {atual === 'revisao' && (
          <>
            <h2>Confira antes de enviar</h2>
            <p className="lead">É com esses dados que a procuração vai ser gerada. Se algo estiver errado, clique em Editar no bloco.</p>
            {blocosRevisao.map((b) => (
              <Revisao key={b.id}>
                <div className="cab"><h3>{b.titulo}</h3><Botao type="button" className="sec" onClick={() => { setVoltarRevisao(true); ir(b.id); }}>Editar</Botao></div>
                {b.linhas.map((l, n) => (l.secao
                  ? <div className="subtitulo" key={`s${n}`}>{l.r}</div>
                  : <div className="linha" key={`l${n}`}><span className="r">{l.r}</span><span>{l.v}</span></div>))}
              </Revisao>
            ))}
            <Emails>
              <Campo>
                <label className="rot" htmlFor="pn_email_extra">
                  {nProc > 1 ? 'As procurações vão' : 'A procuração vai'} para {contexto.email ? <>o e-mail <b>{contexto.email}</b></> : 'o seu e-mail cadastrado'}. Quer que mais alguém receba? Digite o e-mail aqui:
                </label>
                <div className="linha-email">
                  <input id="pn_email_extra" type="email" value={emailNovo} placeholder="nome@email.com" onChange={(e) => { setEmailNovo(e.target.value); setErroEmail(''); }} />
                  <Botao type="button" className="sec" onClick={adicionarEmail}>Adicionar</Botao>
                </div>
                {erroEmail && <p className="erro">{erroEmail}</p>}
              </Campo>
              {emailsExtras.map((e) => (
                <p className="item" key={e}><span>{e}</span><Botao type="button" className="link" onClick={() => setEmailsExtras((l) => l.filter((x) => x !== e))}>Remover</Botao></p>
              ))}
            </Emails>
          </>
        )}

        {atual === 'fim' && (
          <Fim>
            <h2>Pronto! Recebemos os dados.</h2>
            <p className="lead">
              {nProc > 1 ? 'As procurações ficam prontas' : 'A procuração fica pronta'} em até 10 minutos, aqui na Central. Vamos mandar para {contexto.email ? <>o e-mail <b>{contexto.email}</b></> : 'o seu e-mail cadastrado'}
              {emailsExtras.length ? ` e para ${emailsExtras.join(', ')}` : ''} — confira também a caixa de spam.
            </p>
            {nProc > 1 && <Aviso>Vamos gerar uma procuração para cada um. Cada um assina a sua e reconhece a firma por autenticidade.</Aviso>}
            <p>Dúvidas: atendimento.servicos@cidadaniaja.com.br ou WhatsApp (21) 99025-7203.</p>
            <Botao as="a" href="/">Voltar para meus pedidos</Botao>
          </Fim>
        )}

        {atual !== 'fim' && (
          <Rodape>
            {tentou[atual] && nFaltam > 0 && <p className="faltam" role="alert">{nFaltam === 1 ? 'Falta 1 campo — está marcado em vermelho.' : `Faltam ${nFaltam} campos — estão marcados em vermelho.`}</p>}
            {erroEnvio && <p className="faltam" role="alert">{erroEnvio}</p>}
            <div>{ordem.indexOf(atual) > 0 && <Botao type="button" className="link" onClick={() => { setVoltarRevisao(false); ir(ordem[ordem.indexOf(atual) - 1]); }}>Voltar</Botao>}</div>
            <Botao type="button" onClick={continuar} disabled={enviando}>
              {atual === 'inicio' ? 'Começar' : atual === 'revisao' ? (enviando ? 'Enviando…' : 'Enviar') : voltarRevisao ? 'Salvar e voltar para a revisão' : 'Continuar'}
            </Botao>
          </Rodape>
        )}
      </Cartao>
    </Caixa>
  );
}
